import Box from '@/common/components/Box'
import { ErrorMessageWrapper } from '@/common/components/ErrorMessageWrapper'
import { ShowHeader } from '@/common/components/ShowHeader'
import { dateTransformer } from '@/common/utils/dateTransformer'
import { formatCurrency, formatDate } from '@/common/utils/formatters'
import useBillingBill from '@/features/billingBills/hooks/useBillingBill'
import { useMineCompany } from '@/features/company/hooks/useMineCompany'
import { ResumeItem } from '@/features/paymentAdvances/components/ResumeItem'
import { usePaymentAdvance } from '@/features/paymentAdvances/hooks/usePaymentAdvance'
import { BadgeAlertIcon, BadgeCheckIcon, BadgeXIcon } from 'lucide-react'
import { useParams } from 'react-router-dom'

const PaymentAdvanceShow = () => {
  const { id } = useParams()

  const { 
    data: paymentAdvance, 
    isPending: paymentAdvanceIsPending,
    error: paymentAdvanceError = {} as any,
    refetch: refetchPaymentAdvance,
  } = usePaymentAdvance(id || '')

  const { data: bill } = useBillingBill({ id: paymentAdvance?.bill_id, enabled: !!paymentAdvance?.bill_id })

  const { data: company } = useMineCompany()

  if (paymentAdvanceIsPending) return <Box>Carregando...</Box>

  return (
    <div>
      <ShowHeader goBack title="Antecipar recebíveis" />

      <ErrorMessageWrapper
        className='h-[calc((100vh-104px-1.5rem))]'
        statusCode={paymentAdvanceError?.status}
        refetchFunction={refetchPaymentAdvance}
      >
        <Box className="max-h-[calc((100vh-104px-1.5rem))] overflow-y-auto space-y-6">
          <div className="space-y-4">
            {paymentAdvance?.status === 'pending' && (
              <div className="flex items-center gap-1 text-warning">
                <BadgeAlertIcon strokeWidth={1} />
                <span className="font-bold">Adiantamento pendente</span>
              </div>
            )}

            {paymentAdvance?.status === 'approved' && (
              <div className="flex items-center gap-1 text-success">
                <BadgeCheckIcon strokeWidth={1} />
                <span className="font-bold">Adiantamento aprovado</span>
              </div>
            )}

            {paymentAdvance?.status === 'rejected' && (
              <div className="flex items-center gap-1 text-danger">
                <BadgeXIcon strokeWidth={1} />
                <span className="font-bold">Adiantamento rejeitado</span>
              </div>
            )}

            {paymentAdvance?.reject_reason && (
              <div className="w-full h-20 bg-[#FCCDCA33] rounded-lg overflow-y-auto p-4">
                <p className="text-white-800 text-sm">{paymentAdvance.reject_reason}</p>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <p>Detalhes da fatura</p>

            <div className="flex gap-4">
              <div>
                <p className="text-sm font-light italic">Fatura {bill?.number}</p>

                <p className="text-lg font-bold">
                  {bill?.opened_at ? formatDate(new Date(bill?.opened_at)) : ''} -{' '}
                  {bill?.closed_at ? formatDate(new Date(bill?.closed_at)) : ''}
                </p>
              </div>

              <div>
                <p className="text-sm font-light italic">Saldo da fatura</p>

                <p className="text-lg font-bold">{formatCurrency(Number(bill?.total_amount || 0))}</p>
              </div>

              <div>
                <p className="text-sm font-light italic">Data para recebimento</p>

                <p className="text-lg font-bold">{bill?.due_at ? formatDate(new Date(bill.due_at)) : '-'}</p>
              </div>
            </div>
          </div>

          <div className="grid w-full grid-cols-2 gap-4">
            <ResumeItem
              label="Valor solicitado para antecipação"
              value={formatCurrency(Number(paymentAdvance?.discounted_from_bill ?? 0))}
            />

            <ResumeItem
              label="Data para recebimento"
              value={paymentAdvance?.date_to_be_paid ? dateTransformer(paymentAdvance?.date_to_be_paid) : ''}
            />
          </div>

          <div className="space-y-2">
            <p>Cálculo de juros</p>

            <ResumeItem label="Juros ao dia" value={`${Number(paymentAdvance?.applied_daily_interest ?? 0) * 100}%`} />
          </div>

          <div className="w-full h-px bg-white-300" />

          <div className="grid grid-cols-2 gap-4 items-start">
            <ResumeItem
              label="Valor a receber antecipado"
              value={formatCurrency(Number(paymentAdvance?.total_to_be_paid ?? 0))}
            />

            <ResumeItem
              label="Valor a pagar de juros"
              value={`${formatCurrency(Number(paymentAdvance?.interest_total ?? 0))} (${Number(paymentAdvance?.interest_percentage ?? 0) * 100
                }%)`}
              className="bg-yellow-op-2"
            />
          </div>

          <div className="grid w-full grid-cols-2 gap-4">
            <ResumeItem
              label="Valor a ser abatido da fatura"
              value={formatCurrency(Number(paymentAdvance?.discounted_from_bill ?? 0))}
            />
          </div>

          {company?.matrix_branch?.bank_account_information && (
            <Box className="space-y-4 p-4">
              <p>Detalhes bancários</p>

              <div className="grid w-full grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-light italic">Nome do banco</p>

                  <p className="text-lg font-bold">
                    {company?.matrix_branch?.bank_account_information?.bank_institution?.name}
                  </p>
                </div>

                <div>
                  <p className="text-sm font-light italic">Número do banco</p>

                  <p className="text-lg font-bold">
                    {company?.matrix_branch?.bank_account_information?.bank_institution?.code}
                  </p>
                </div>

                <div>
                  <p className="text-sm font-light italic">Agência (com dígito)</p>

                  <p className="text-lg font-bold">{company?.matrix_branch?.bank_account_information?.agency}</p>
                </div>

                <div>
                  <p className="text-sm font-light italic">Conta (com dígito)</p>

                  <p className="text-lg font-bold">{company?.matrix_branch?.bank_account_information?.account}</p>
                </div>

                <div>
                  <p className="text-sm font-light italic">Favorecido</p>

                  <p className="text-lg font-bold">{company?.matrix_branch?.bank_account_information?.payee_name}</p>
                </div>
              </div>
            </Box>
          )}
        </Box>
      </ErrorMessageWrapper>
    </div>
  )
}

export default PaymentAdvanceShow
